import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Shield, Crown, User, Calendar, X, Car, Star, Archive, Edit3, Check, Loader2, ExternalLink, MessageSquare, Files, Globe, RotateCcw } from 'lucide-react';
import { callRpc } from '../../lib/rpcClient';
import { format, formatDistanceToNow } from 'date-fns';
import { AlertModal } from '../../components/ui/AlertModal';

type TabFilter = 'all' | 'new_24h' | 'new_7d' | 'active_1h' | 'active_24h' | 'active_7d';

const TABS: { id: TabFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'new_24h', label: 'New 24h' },
  { id: 'new_7d', label: 'New 7d' },
  { id: 'active_1h', label: 'Active 1h' },
  { id: 'active_24h', label: 'Active 24h' },
  { id: 'active_7d', label: 'Active 7d' },
];

const PLAN_LIMITS: Record<string, Record<string, number>> = {
  free: { 
    storage_size_limit: 100, 
    file_size_limit: 25, 
    daily_upload_limit: 50, 
    hourly_upload_limit: 20, 
    file_count_limit: 10, 
    email_parsing_limit: 20, 
    file_parsing_limit: 20, 
    car_creation_limit: 1 
  },
  pro: { 
    storage_size_limit: 2048, 
    file_size_limit: 50, 
    daily_upload_limit: 100, 
    hourly_upload_limit: 50, 
    file_count_limit: 20, 
    email_parsing_limit: 75, 
    file_parsing_limit: 50, 
    car_creation_limit: 3 
  }
};

function bytesToMb(bytes: number | string) {
  const b = typeof bytes === 'string' ? parseInt(bytes) : bytes;
  if (!b) return 0;
  return Math.round(b / 1048576);
}

function mbToBytes(mb: string | number) {
  const m = typeof mb === 'string' ? parseInt(mb) : mb;
  if (!m) return 0;
  return m * 1048576;
}

function formatBytes(bytes: number) {
  if (!bytes) return '0 B';
  const gb = bytes / 1073741824;
  if (gb >= 1) return gb.toFixed(2) + ' GB';
  const mb = bytes / 1048576;
  if (mb >= 1) return mb.toFixed(1) + ' MB';
  return (bytes / 1024).toFixed(0) + ' KB';
}

function UserDetailModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPrefs, setEditingPrefs] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [useDefault, setUseDefault] = useState<Record<string, boolean>>({});
  const [resetDaily, setResetDaily] = useState(false);
  const [resetHourly, setResetHourly] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ 
      isOpen: boolean; 
      title: string; 
      message: string;
      confirmLabel?: string;
  }>({ isOpen: false, title: '', message: '' });

  const startEditing = () => {
    const p: any = detail?.profile || {};
    const currentPlan = p.plan_type || 'free';
    
    const fields = [
      'car_creation_limit', 'file_count_limit', 'storage_size_limit', 
      'file_size_limit', 'daily_upload_limit', 'hourly_upload_limit', 
      'email_parsing_limit', 'file_parsing_limit'
    ];

    const vals: any = { plan_type: currentPlan };
    const defaults: Record<string, boolean> = {};

    fields.forEach(f => {
      let v = p[f];
      if (v === 0 || v === null || v === undefined) {
        defaults[f] = true;
        // Show current plan default in the input
        let defVal = PLAN_LIMITS[currentPlan]?.[f] || 0;
        vals[f] = String(f.includes('size_limit') ? defVal : defVal);
      } else {
        defaults[f] = false;
        vals[f] = String(f.includes('size_limit') ? bytesToMb(v) : v);
      }
    });

    setFormValues(vals);
    setUseDefault(defaults);
    setResetDaily(false);
    setResetHourly(false);
    setEditingPrefs(true);
  };

  useEffect(() => {
    callRpc('get_admin_user_detail', { p_user_id: userId })
      .then(d => setDetail(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const handleSavePrefs = async () => {
    setSaving(true);
    try {
      const getVal = (key: string) => useDefault[key] ? 0 : (parseInt(formValues[key]) || 0);
      const getBytes = (key: string) => useDefault[key] ? 0 : mbToBytes(formValues[key]);

      await callRpc('update_admin_user_preferences', {
        p_user_id: userId,
        p_plan_type: formValues.plan_type,
        p_car_creation_limit: getVal('car_creation_limit'),
        p_storage_size_limit: getBytes('storage_size_limit'),
        p_file_size_limit: getBytes('file_size_limit'),
        p_daily_upload_limit: getVal('daily_upload_limit'),
        p_hourly_upload_limit: getVal('hourly_upload_limit'),
        p_file_count_limit: getVal('file_count_limit'),
        p_email_parsing_limit: getVal('email_parsing_limit'),
        p_file_parsing_limit: getVal('file_parsing_limit'),
        p_reset_daily: resetDaily,
        p_reset_hourly: resetHourly,
      });
      setEditingPrefs(false);
      const updated = await callRpc('get_admin_user_detail', { p_user_id: userId });
      setDetail(updated);
    } catch (err: any) {
      setAlertConfig({
        isOpen: true,
        title: 'Save failed',
        message: err.message || 'Unknown error occurred',
        confirmLabel: 'Labi'
      });
    } finally {
      setSaving(false);
    }
  };

  const applyPlanDefaults = (plan: string) => {
    setFormValues((prev: Record<string, any>) => {
      const next: any = { ...prev, plan_type: plan };
      // Update values for fields that are set to default
      Object.keys(useDefault).forEach(key => {
        if (useDefault[key]) {
          next[key] = String(PLAN_LIMITS[plan]?.[key] || 0);
        }
      });
      return next;
    });
  };

  const inputCls = "w-full bg-slate-950 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all";

  if (loading) return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
    </div>
  );

  const p = detail?.profile || {};
  const s = detail?.car_stats || {};
  const ev = detail?.events_summary || {};
  const cars = detail?.cars || [];

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4 font-primary">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl my-8 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800 shrink-0">
          <div>
            <h2 className="text-xl font-black text-white">{p.email}</h2>
            <div className="flex items-center gap-3 mt-1.5">
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-lg border ${p.plan_type === 'pro' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                {p.plan_type?.toUpperCase() || 'FREE'}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-500 font-bold">
                <Globe size={13} className="text-slate-600" />
                {p.language?.toUpperCase() || '--'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-8 overflow-y-auto flex-1">
          {/* Vehicle Stats */}
          <section>
            <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-4 flex items-center gap-2">
              <Car size={14} /> Overall Statistics
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Active', value: s.active_cars, color: 'text-blue-400' },
                { label: 'Archived', value: s.archived_cars, color: 'text-amber-400' },
                { label: 'Favorites', value: s.favorite_cars, color: 'text-yellow-400' },
                { label: 'Car Admin', value: s.car_admin_count, color: 'text-purple-400' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-slate-950/60 border border-slate-800/80 rounded-2xl p-4">
                  <div className="text-slate-500 text-[10px] uppercase font-bold mb-1 tracking-tight">{label}</div>
                  <div className={`text-2xl font-black leading-none ${color}`}>{value ?? 0}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Preferences Editor */}
          <section>
            <div className="flex items-center justify-between mb-4">
               <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider flex items-center gap-2">
                <Shield size={14} /> Plan & Subscription
              </h3>
              <div className="flex gap-2">
                {editingPrefs ? (
                  <>
                    <button onClick={() => setEditingPrefs(false)} className="text-xs px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors font-bold">
                      Cancel
                    </button>
                    <button onClick={handleSavePrefs} disabled={saving} className="text-xs px-4 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors font-bold flex items-center gap-1.5 disabled:opacity-60 shadow-lg shadow-blue-500/20">
                      {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                      Save Changes
                    </button>
                  </>
                ) : (
                  <button onClick={startEditing} className="text-xs px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors font-bold flex items-center gap-1.5">
                    <Edit3 size={13} />
                    Edit Limits
                  </button>
                )}
              </div>
            </div>

            {editingPrefs ? (
              <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-5 space-y-5">
                <div>
                  <label className="text-[11px] uppercase font-bold text-slate-500 block mb-2 px-1">Plan Tier</label>
                  <div className="flex gap-2 p-1 bg-slate-900 rounded-xl">
                    {['free', 'pro'].map(plan => (
                      <button key={plan} onClick={() => applyPlanDefaults(plan)}
                        className={`flex-1 py-1.5 rounded-lg font-black capitalize text-xs transition-all ${formValues.plan_type === plan ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-300'}`}>
                        {plan}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'car_creation_limit', label: 'Car Creation Limit' },
                    { key: 'file_count_limit', label: 'Total Files Limit' },
                    { key: 'storage_size_limit', label: 'Storage Limit (MB)', placeholder: 'e.g. 50' },
                    { key: 'file_size_limit', label: 'Max File Size (MB)', placeholder: 'e.g. 10' },
                    { key: 'daily_upload_limit', label: 'Daily Uploads' },
                    { key: 'hourly_upload_limit', label: 'Hourly Uploads' },
                    { key: 'email_parsing_limit', label: 'Email Parse Limit' },
                    { key: 'file_parsing_limit', label: 'File Parse Limit' },
                  ].map(({ key, label, placeholder }) => {
                    const isDefault = useDefault[key];
                    return (
                      <div key={key} className="relative group">
                        <div className="flex justify-between items-center mb-1.5 px-1">
                          <label className="text-[11px] uppercase font-bold text-slate-500 block">{label}</label>
                          <label className="flex items-center gap-1.5 cursor-pointer select-none group/default">
                            <span className="text-[9px] font-black uppercase text-slate-600 group-hover/default:text-blue-400 Transition-colors">Default</span>
                            <div 
                              onClick={() => {
                                const newDef = !isDefault;
                                setUseDefault((prev: Record<string, boolean>) => ({ ...prev, [key]: newDef }));
                                if (newDef) {
                                  setFormValues((prev: Record<string, any>) => ({ ...prev, [key]: String(PLAN_LIMITS[formValues.plan_type]?.[key] || 0) }));
                                }
                              }}
                              className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${isDefault ? 'bg-blue-600 border-blue-500' : 'bg-slate-900 border-slate-700'}`}
                            >
                              {isDefault && <Check size={12} className="text-white" />}
                            </div>
                          </label>
                        </div>
                        <input
                          type="number"
                          placeholder={placeholder}
                          value={formValues[key]}
                          disabled={isDefault}
                          onChange={e => setFormValues(prev => ({ ...prev, [key]: e.target.value }))}
                          className={`${inputCls} ${isDefault ? 'opacity-50 grayscale select-none border-dashed' : ''}`}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-4 pt-2">
                  <button
                    onClick={() => setResetDaily(!resetDaily)}
                    className={`flex-1 py-2 px-3 rounded-xl border-2 font-black text-[10px] uppercase tracking-wider transition-all ${resetDaily ? 'bg-red-500/10 border-red-500 text-red-500' : 'bg-slate-900 border-slate-800 text-slate-500'}`}
                  >
                    Reset Daily Counter {resetDaily && '✓'}
                  </button>
                  <button
                    onClick={() => setResetHourly(!resetHourly)}
                    className={`flex-1 py-2 px-3 rounded-xl border-2 font-black text-[10px] uppercase tracking-wider transition-all ${resetHourly ? 'bg-red-500/10 border-red-500 text-red-500' : 'bg-slate-900 border-slate-800 text-slate-500'}`}
                  >
                    Reset Hourly Counter {resetHourly && '✓'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-950/60 border border-slate-800 rounded-2xl divide-y divide-slate-800/60 overflow-hidden">
                {[
                  { label: 'Subscription Plan', value: <span className={`font-black px-2 py-0.5 rounded text-xs ${(p.plan_type || 'free') === 'pro' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-800 text-slate-300'}`}>{p.plan_type?.toUpperCase() || 'FREE'}</span> },
                  { 
                    label: 'Vehicle Limit', 
                    value: (
                      <span className="text-white font-bold">
                        {(!p.car_creation_limit || p.car_creation_limit === 0) ? <span className="text-slate-500 italic font-medium mr-1">(default) -</span> : ''}
                        {p.car_creation_limit || PLAN_LIMITS[p.plan_type || 'free'].car_creation_limit} cars
                      </span>
                    ) 
                  },
                  { label: 'Storage Used', value: <span className="text-blue-400 font-bold">{formatBytes(p.used_storage_bytes || 0)}</span> },
                  { label: 'Pending (Temp)', value: <span className="text-slate-500 font-bold">{formatBytes(p.temp_storage_bytes || 0)}</span> },
                  { 
                    label: 'Storage Allowance', 
                    value: (
                      <span className="text-slate-200">
                        {(!p.storage_size_limit || p.storage_size_limit === 0) ? <span className="text-slate-500 italic font-medium mr-1">(default) -</span> : ''}
                        {formatBytes(p.storage_size_limit || mbToBytes(PLAN_LIMITS[p.plan_type || 'free'].storage_size_limit))}
                      </span>
                    )
                  },
                  { 
                    label: 'Max File Size', 
                    value: (
                      <span className="text-slate-200">
                        {(!p.file_size_limit || p.file_size_limit === 0) ? <span className="text-slate-500 italic font-medium mr-1">(default) -</span> : ''}
                        {formatBytes(p.file_size_limit || mbToBytes(PLAN_LIMITS[p.plan_type || 'free'].file_size_limit))}
                      </span>
                    )
                  },
                  { 
                    label: 'Email Parse Limit', 
                    value: (
                      <span className="text-slate-200">
                        {(!p.email_parsing_limit || p.email_parsing_limit === 0) ? <span className="text-slate-500 italic font-medium mr-1">(default) -</span> : ''}
                        {p.email_parsing_limit || PLAN_LIMITS[p.plan_type || 'free'].email_parsing_limit} per month
                      </span>
                    )
                  },
                  { 
                    label: 'File Parse Limit', 
                    value: (
                      <span className="text-slate-200">
                        {(!p.file_parsing_limit || p.file_parsing_limit === 0) ? <span className="text-slate-500 italic font-medium mr-1">(default) -</span> : ''}
                        {p.file_parsing_limit || PLAN_LIMITS[p.plan_type || 'free'].file_parsing_limit} per month
                      </span>
                    )
                  },
                  { 
                    label: 'Daily Uploads', 
                    value: (
                      <div className="flex flex-col items-end">
                        <div className="flex items-center gap-1.5">
                          {(!p.daily_upload_limit || p.daily_upload_limit === 0) && <span className="text-slate-500 italic font-medium text-[10px]">(default) -</span>}
                          <span className="font-bold text-white">
                            {p.daily_uploads_count ?? 0} / {p.daily_upload_limit || PLAN_LIMITS[p.plan_type || 'free'].daily_upload_limit}
                          </span>
                        </div>
                        {p.daily_uploads_reset_at && <span className="text-[10px] text-slate-600">Reset: {format(new Date(p.daily_uploads_reset_at), 'MMM dd HH:mm')}</span>}
                      </div>
                    )
                  },
                  { 
                    label: 'Hourly Uploads', 
                    value: (
                      <div className="flex flex-col items-end">
                        <div className="flex items-center gap-1.5">
                          {(!p.hourly_upload_limit || p.hourly_upload_limit === 0) && <span className="text-slate-500 italic font-medium text-[10px]">(default) -</span>}
                          <span className="font-bold text-white">
                            {p.hourly_uploads_count ?? 0} / {p.hourly_upload_limit || PLAN_LIMITS[p.plan_type || 'free'].hourly_upload_limit}
                          </span>
                        </div>
                        {p.hourly_uploads_reset_at && <span className="text-[10px] text-slate-600">Reset: {format(new Date(p.hourly_uploads_reset_at), 'MMM dd HH:mm')}</span>}
                      </div>
                    )
                  },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center px-5 py-3">
                    <span className="text-slate-400 text-sm font-medium">{label}</span>
                    <span className="text-slate-200 text-sm">{value}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Vehicles Detailed List */}
          {cars.length > 0 && (
            <section>
              <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-4 flex items-center gap-2">
                <ExternalLink size={14} /> Vehicles Explorer
              </h3>
              <div className="space-y-3">
                {cars.map((car: any) => (
                  <div key={car.car_id} className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 group hover:bg-slate-800/30 transition-all border-l-2 border-l-transparent hover:border-l-blue-500">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-0.5">
                           {car.is_archived && <Archive size={14} className="text-amber-500" />}
                           {car.is_favorite && <Star size={14} className="text-yellow-400 fill-yellow-400" />}
                           <span className="text-white font-black text-[15px]">{car.name || `${car.make} ${car.model}`}</span>
                        </div>
                        <div className="text-slate-500 text-xs font-bold uppercase tracking-tighter">
                          {car.make} {car.model} • <span className="text-slate-600">ID: {car.car_id.split('-')[0]}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-5 sm:gap-8 overflow-x-auto pb-1 sm:pb-0">
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] uppercase font-bold text-slate-600 mb-0.5">Entries</span>
                        <div className="flex items-center gap-1 text-slate-300 font-black text-sm"><MessageSquare size={13} className="text-blue-500" /> {car.log_entries_count}</div>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] uppercase font-bold text-slate-600 mb-0.5">Notes</span>
                        <div className="flex items-center gap-1 text-slate-300 font-black text-sm"><Shield size={13} className="text-purple-500" /> {car.notes_count}</div>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] uppercase font-bold text-slate-600 mb-0.5">Files</span>
                        <div className="flex items-center gap-1 text-slate-300 font-black text-sm"><Files size={13} className="text-emerald-500" /> {car.files_count}</div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-[10px] uppercase font-bold text-slate-600 mb-0.5">Last Activity</span>
                        <div className="text-slate-400 text-xs font-bold whitespace-nowrap">
                          {car.last_entry_at ? format(new Date(car.last_entry_at), 'MMM dd, yy') : 'no activity'}
                        </div>
                      </div>
                    </div>

                    <a
                      href={`/admin/${car.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-slate-900 rounded-xl text-slate-400 hover:text-blue-400 border border-slate-800/80 hover:border-blue-500/30 transition-all flex items-center justify-center"
                    >
                      <ExternalLink size={18} />
                    </a>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Activity Feed Summary */}
          <section>
            <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-4 flex items-center gap-2">
              <Calendar size={14} /> Journey Timeline
            </h3>
            <div className="bg-slate-950/60 border border-slate-800 rounded-2xl divide-y divide-slate-800/60 overflow-hidden">
              {[
                { label: 'Registered', val: ev.registered_at || p.created_at },
                { label: 'First Vehicle Added', val: ev.first_car_at },
                { label: 'First Expense Logged', val: ev.first_expense_at },
                { label: 'First Email Ingested', val: ev.first_email_ingestion_at },
                { label: 'Last System Activity', val: p.last_activity_at },
              ].map(({ label, val }) => (
                <div key={label} className="flex justify-between items-center px-5 py-3">
                  <span className="text-slate-400 text-sm font-medium">{label}</span>
                  <span className="text-slate-200 text-xs font-bold">
                    {val ? format(new Date(val), 'MMMM dd, yyyy HH:mm') : <span className="text-slate-700 italic font-medium">never</span>}
                  </span>
                </div>
              ))}
              <div className="flex justify-between items-center px-5 py-3 bg-slate-900/30">
                <span className="text-slate-400 text-sm font-bold uppercase tracking-tight">Total Touchpoints</span>
                <span className="text-blue-400 font-black text-lg leading-none">{ev.total_events || 0}</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [data, setData] = useState<{ users: any[]; total: number }>({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [alertConfig, setAlertConfig] = useState<{ 
      isOpen: boolean; 
      title: string; 
      message: string;
      confirmLabel?: string;
  }>({ isOpen: false, title: '', message: '' });

  const page = parseInt(searchParams.get('page') || '1', 10);
  const searchStr = searchParams.get('search') || '';
  const activeTab = (searchParams.get('tab') || 'all') as TabFilter;
  const limit = 20;

  const [searchInput, setSearchInput] = useState(searchStr);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await callRpc('get_admin_users_overview', {
        p_limit: limit,
        p_offset: (page - 1) * limit,
        p_search: searchStr,
        p_filter: activeTab,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, searchStr, activeTab]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(prev => { prev.set('search', searchInput); prev.set('page', '1'); return prev; });
  };

  const setTab = (tab: TabFilter) => {
    setSearchParams(prev => { prev.set('tab', tab); prev.set('page', '1'); return prev; });
  };

  const totalPages = Math.ceil(data.total / limit);

  return (
    <div className="space-y-6 font-primary pb-12">
      {selectedUserId && (
        <UserDetailModal userId={selectedUserId} onClose={() => { setSelectedUserId(null); loadData(); }} />
      )}

      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-white mb-1">User Management</h1>
          <p className="text-slate-500 font-bold">Monitor <span className="text-white">{data.total}</span> registered accounts.</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1.5 bg-slate-900 border border-slate-800 rounded-2xl p-1.5 overflow-x-auto shadow-inner no-scrollbar">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setTab(tab.id)}
            className={`px-4 py-2 rounded-xl font-black text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl">
        {/* Toolbar */}
        <div className="p-5 border-b border-slate-800/60 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-900/50">
          <form onSubmit={handleSearch} className="w-full sm:max-w-md relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={18} />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Filter by email address..."
              className="w-full bg-slate-950 border border-slate-800 text-white rounded-2xl pl-12 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-all font-bold text-sm shadow-inner"
            />
          </form>

          <div className="flex items-center gap-4 text-slate-400">
            <span className="text-xs font-black uppercase tracking-widest border border-slate-800 bg-slate-950 px-4 py-2 rounded-xl">
              Page {page} / {Math.max(1, totalPages)}
            </span>
            <div className="flex gap-2">
              <button 
                onClick={() => loadData()}
                disabled={loading}
                className="p-2.5 border border-slate-800 bg-slate-950 rounded-xl hover:bg-slate-800 disabled:opacity-30 transition-all shadow-lg active:scale-95 text-slate-400"
                title="Refresh"
              >
                <RotateCcw size={20} className={loading ? 'animate-spin' : ''} />
              </button>
              <button onClick={() => setSearchParams(prev => { prev.set('page', String(Math.max(1, page - 1))); return prev; })} disabled={page <= 1 || loading} className="p-2.5 border border-slate-800 bg-slate-950 rounded-xl hover:bg-slate-800 disabled:opacity-30 transition-all shadow-lg active:scale-95">
                <ChevronLeft size={20} />
              </button>
              <button onClick={() => setSearchParams(prev => { prev.set('page', String(Math.min(totalPages, page + 1))); return prev; })} disabled={page >= totalPages || loading} className="p-2.5 border border-slate-800 bg-slate-950 rounded-xl hover:bg-slate-800 disabled:opacity-30 transition-all shadow-lg active:scale-95">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {error ? (
            <div className="p-12 text-center text-red-400 font-bold">{error}</div>
          ) : loading ? (
            <div className="p-20 flex justify-center flex-col items-center gap-4 text-slate-500 font-bold uppercase text-xs tracking-widest">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              Refreshing users...
            </div>
          ) : data.users.length === 0 ? (
            <div className="p-20 text-center text-slate-600 font-black italic tracking-wide">No accounts found matching criteria.</div>
          ) : (
            <table className="w-full text-left text-sm text-slate-300">
              <thead className="bg-slate-950/80 text-slate-500 text-[10px] uppercase font-black tracking-widest border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4">Identity</th>
                  <th className="px-6 py-4 text-center">Settings</th>
                  <th className="px-6 py-4 text-center hidden md:table-cell">Assets</th>
                  <th className="px-6 py-4">Account Age</th>
                  <th className="px-6 py-4 hidden lg:table-cell">Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40">
                {data.users.map((user: any) => (
                  <tr
                    key={user.user_id}
                    onClick={() => setSelectedUserId(user.user_id)}
                    className="hover:bg-slate-800/30 transition-all cursor-pointer group"
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-2xl flex-shrink-0 transition-all group-hover:scale-110 ${user.is_admin ? 'bg-red-500/10 text-red-500' : 'bg-blue-500/10 text-blue-400'}`}>
                          {user.is_admin ? <Shield size={20} /> : <User size={20} />}
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <span className="font-black text-white text-[15px] truncate max-w-[240px] leading-tight">
                            {user.email}
                          </span>
                          {user.is_admin && <span className="text-red-500 text-[9px] font-black uppercase tracking-widest leading-none">Super Admin</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex flex-col items-center gap-1.5">
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-[10px] font-black tracking-wider border transition-all ${user.plan_type === 'pro' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20 shadow-lg shadow-yellow-500/5' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                          {user.plan_type === 'pro' && <Crown size={12} />}
                          {(user.plan_type || 'free').toUpperCase()}
                        </div>
                        <div className="flex items-center gap-1 text-slate-500 font-bold text-[10px] uppercase">
                          <Globe size={11} className="text-slate-700" />
                          {user.language || '--'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-center hidden md:table-cell">
                      <div className="flex items-center justify-center gap-4 text-[13px] font-black text-slate-400">
                        <div title="Active" className="flex flex-col items-center"><span className="text-blue-400">{user.cars_count}</span><span className="text-[8px] uppercase text-slate-700">Cars</span></div>
                        <div title="Favorites" className="flex flex-col items-center"><span className="text-yellow-500">{user.favorite_cars_count}</span><span className="text-[8px] uppercase text-slate-700">Favs</span></div>
                        <div title="Car Admin" className="flex flex-col items-center"><span className="text-purple-400">{user.car_admin_count}</span><span className="text-[8px] uppercase text-slate-700">Roles</span></div>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-slate-500 whitespace-nowrap">
                      <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-tight">
                         <Calendar size={13} className="text-slate-700" />
                         {format(new Date(user.created_at), 'MMM dd, yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-5 text-slate-500 hidden lg:table-cell whitespace-nowrap text-xs font-bold">
                      {user.last_activity_at
                        ? <span className="text-blue-400/80">{formatDistanceToNow(new Date(user.last_activity_at), { addSuffix: true })}</span>
                        : <span className="text-slate-800 italic">No activity record</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <AlertModal
          {...alertConfig}
          onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
        />
      </div>
    </div>
  );
}
